import React, { useState, useEffect, useRef } from 'react';
import { Plus, ArrowRight, ArrowLeft, Package, Wand2, ShoppingCart, Check, Edit3, Trash2, Loader, Search, AlertCircle } from 'lucide-react';
import apinode from '../../services/apinode'; // ajuste o path conforme seu projeto

const KitCreator = () => {
  const [availableItems, setAvailableItems] = useState([]);
  const [loading, setLoading] = useState(false);
  const [apiError, setApiError] = useState(null);

  const [currentStep, setCurrentStep] = useState('start'); // start, creating, review, complete
  const [currentKit, setCurrentKit] = useState({
    items: [],
    currentItemIndex: 0,
    generatedName: '',
    totalPrice: 0
  });
  const [completedKits, setCompletedKits] = useState([]);
  const [searchTerm, setSearchTerm] = useState('');
  const [apiPath, setApiPath] = useState('/products'); // << agora usamos endpoint do apinode
  const searchTimeoutRef = useRef(null);

  // -------- Buscar produtos no APINODE --------
  const searchProductsInAPI = async (searchQuery = '') => {
    if (!apiPath.trim() || !apiPath.startsWith('/')) {
      setApiError('Defina um endpoint válido (ex.: /products ou /produtos)');
      return;
    }

    setLoading(true);
    setApiError(null);

    try {
      // Alguns backends aceitam apenas 1 param. Mandamos vários; o backend ignora os desconhecidos.
      const params = {};
      if (searchQuery.trim()) {
        params.search = searchQuery.trim();
        params.q = searchQuery.trim();
        params.query = searchQuery.trim();
      }

      const response = await apinode.get(apiPath, { params });
      const data = response?.data;

      // Processar diferentes formatos de resposta
      let products = [];
      if (Array.isArray(data)) {
        products = data;
      } else if (data?.products && Array.isArray(data.products)) {
        products = data.products;
      } else if (data?.data && Array.isArray(data.data)) {
        products = data.data;
      } else if (data?.items && Array.isArray(data.items)) {
        products = data.items;
      } else if (data?.results && Array.isArray(data.results)) {
        products = data.results;
      }

      // Normalizar
      const normalizedProducts = products.map((product, index) => {
        const id = product.id || product._id || product.productId || product.codigo || product.code || product.sku || (index + 1);
        const name = product.name || product.title || product.productName || product.descricao || product.description || 'Produto sem nome';
        const rawPrice = product.price ?? product.cost ?? product.value ?? product.amount ?? product.preco ?? product.valor ?? 0;
        const price = Number.isFinite(rawPrice) ? Number(rawPrice) : parseFloat(String(rawPrice).replace(',', '.')) || 0;
        const category = product.category || product.tipo || product.type || product.group || 'Geral';

        const keywords = [
          ...String(name).toLowerCase().split(' '),
          ...String(category).toLowerCase().split(' '),
          ...(product.brand ? String(product.brand).toLowerCase().split(' ') : []),
          ...(product.tags ? product.tags.map((t) => String(t).toLowerCase()) : [])
        ].filter(Boolean);

        return {
          id,
          name,
          category,
          price,
          keywords: [...new Set(keywords)],
          brand: product.brand || product.marca || '',
          description: product.description || product.descricao || '',
          image: product.image || product.thumbnail || product.imagem || '',
          stock: product.stock ?? product.quantity ?? product.estoque ?? 0,
          sku: product.sku || product.code || product.codigo || ''
        };
      });

      setAvailableItems(normalizedProducts);
      setApiError(null);
    } catch (error) {
      console.error('Erro ao buscar produtos:', error);
      setApiError(`Erro ao conectar com o APINODE: ${error?.message || 'verifique o endpoint'}`);
      setAvailableItems([]);
    } finally {
      setLoading(false);
    }
  };

  // -------- Debounce da busca --------
  const handleSearchChange = (value) => {
    setSearchTerm(value);
    if (searchTimeoutRef.current) clearTimeout(searchTimeoutRef.current);
    searchTimeoutRef.current = setTimeout(() => {
      if (apiPath.trim()) searchProductsInAPI(value);
    }, 800);
  };

  // Buscar quando o endpoint mudar
  useEffect(() => {
    if (apiPath.trim()) searchProductsInAPI('');
    return () => {
      if (searchTimeoutRef.current) clearTimeout(searchTimeoutRef.current);
    };
  }, [apiPath]);

  // Filtro local de backup
  const filteredItems = availableItems.filter(item =>
    String(item.name).toLowerCase().includes(searchTerm.toLowerCase()) ||
    String(item.category).toLowerCase().includes(searchTerm.toLowerCase()) ||
    (item.brand && String(item.brand).toLowerCase().includes(searchTerm.toLowerCase())) ||
    item.keywords.some(keyword => keyword.includes(searchTerm.toLowerCase()))
  );

  // -------- Nome inteligente --------
  const generateSmartName = (items) => {
    if (items.length === 0) return '';

    const categories = items.map(item => item.category);
    const mainCategory = categories[0]; // pode ser melhorado se quiser "mais frequente"

    const brands = [];
    items.forEach(item => {
      const nm = `${item.name} ${item.brand}`.toLowerCase();
      if (nm.includes('apple') || nm.includes('iphone') || nm.includes('airpods')) {
        brands.push('Apple');
      } else if (nm.includes('samsung') || nm.includes('galaxy')) {
        brands.push('Samsung');
      }
    });

    const mainBrand = [...new Set(brands)][0] || '';
    const hasSmartphone = items.some(i => /smartphone|phone|celular/i.test(i.category));
    const hasCharger = items.some(i => /carregador|charger/i.test(i.category));
    const hasHeadphone = items.some(i => /fone|headphone|earbud/i.test(i.category));
    const hasCase = items.some(i => /case|proteç|protec/i.test(i.category));

    let name = 'Kit ';
    if (mainBrand) name += `${mainBrand} `;

    if (hasSmartphone) {
      name += 'Completo ';
      if (hasCharger && hasHeadphone && hasCase) name += 'Premium';
      else if (hasCharger && hasCase) name += 'Essencial';
      else name += 'Básico';
    } else {
      name += 'Acessórios ';
      name += items.length >= 3 ? 'Premium' : 'Básico';
    }

    return name.trim();
  };

  // -------- Fluxo de criação --------
  const startCreating = () => {
    setCurrentKit({ items: [], currentItemIndex: 0, generatedName: '', totalPrice: 0 });
    setCurrentStep('creating');
    setSearchTerm('');
  };

  const addItemToKit = (item) => {
    const newItems = [...currentKit.items, { ...item, quantity: 1 }];
    const totalPrice = newItems.reduce((sum, it) => sum + (it.price * it.quantity), 0);
    const generatedName = generateSmartName(newItems);

    setCurrentKit({
      ...currentKit,
      items: newItems,
      currentItemIndex: newItems.length,
      generatedName,
      totalPrice
    });
    setSearchTerm('');
  };

  const goToReview = () => setCurrentStep('review');

  const completeKit = () => {
    const finalKit = { ...currentKit, id: Date.now(), createdAt: new Date().toLocaleString() };
    setCompletedKits([...completedKits, finalKit]);
    setCurrentStep('complete');
  };

  const createAnother = () => setCurrentStep('start');

  const removeItemFromCurrentKit = (itemId) => {
    const newItems = currentKit.items.filter(item => item.id !== itemId);
    const totalPrice = newItems.reduce((sum, it) => sum + (it.price * it.quantity), 0);
    const generatedName = generateSmartName(newItems);

    setCurrentKit({
      ...currentKit,
      items: newItems,
      currentItemIndex: Math.max(0, newItems.length),
      generatedName,
      totalPrice
    });
  };

  const updateQuantity = (itemId, newQuantity) => {
    if (newQuantity < 1) return;
    const newItems = currentKit.items.map(item => item.id === itemId ? { ...item, quantity: newQuantity } : item);
    const totalPrice = newItems.reduce((sum, it) => sum + (it.price * it.quantity), 0);
    const generatedName = generateSmartName(newItems);

    setCurrentKit({ ...currentKit, items: newItems, generatedName, totalPrice });
  };

  // --------- UIs ---------
  const currency = (v) => `R$ ${Number(v || 0).toFixed(2)}`;

  const renderStartScreen = () => (
    <div style={{ textAlign: 'center', maxWidth: '600px', margin: '0 auto' }}>
      <div style={{ marginBottom: '2rem' }}>
        <Wand2 size={64} color="#4f46e5" style={{ margin: '0 auto 1rem' }} />
        <h2 style={{ fontSize: '2rem', fontWeight: 'bold', color: '#1f2937', margin: '0 0 1rem 0' }}>
          Criador Inteligente de Kits
        </h2>
        <p style={{ color: '#6b7280', fontSize: '1.1rem', margin: 0, lineHeight: '1.6' }}>
          Conecte ao seu <strong>apinode</strong> e eu te guio passo a passo para criar o kit perfeito!
        </p>
      </div>

      {/* Configuração do endpoint do apinode */}
      <div style={{
        backgroundColor: 'white',
        padding: '2rem',
        borderRadius: '12px',
        boxShadow: '0 2px 10px rgba(0,0,0,0.1)',
        marginBottom: '2rem'
      }}>
        <h3 style={{ color: '#1f2937', margin: '0 0 1rem 0', display: 'flex', alignItems: 'center', justifyContent: 'center', gap: '0.5rem' }}>
          <ShoppingCart size={20} />
          Configurar endpoint (apinode)
        </h3>

        <div style={{ marginBottom: '1rem' }}>
          <input
            type="text"
            placeholder="/products  |  /produtos"
            value={apiPath}
            onChange={(e) => setApiPath(e.target.value)}
            style={{
              width: '100%',
              padding: '0.75rem 1rem',
              border: '2px solid #e5e7eb',
              borderRadius: '8px',
              fontSize: '1rem',
              outline: 'none',
              boxSizing: 'border-box'
            }}
            onFocus={(e) => e.target.style.borderColor = '#4f46e5'}
            onBlur={(e) => e.target.style.borderColor = '#e5e7eb'}
          />
        </div>

        <div style={{ display: 'flex', gap: '1rem', justifyContent: 'center', alignItems: 'center' }}>
          <button
            onClick={() => searchProductsInAPI('')}
            disabled={!apiPath.trim() || loading}
            style={{
              display: 'flex',
              alignItems: 'center',
              gap: '0.5rem',
              backgroundColor: apiPath.trim() ? '#059669' : '#9ca3af',
              color: 'white',
              padding: '0.75rem 1.5rem',
              border: 'none',
              borderRadius: '8px',
              cursor: apiPath.trim() && !loading ? 'pointer' : 'not-allowed',
              fontWeight: '500'
            }}
          >
            {loading ? <Loader size={16} className="animate-spin" /> : <Search size={16} />}
            {loading ? 'Carregando...' : 'Testar Conexão'}
          </button>

          {availableItems.length > 0 && (
            <span style={{ color: '#059669', fontWeight: '500' }}>
              ✅ {availableItems.length} produtos encontrados
            </span>
          )}
        </div>

        {apiError && (
          <div style={{
            backgroundColor: '#fef2f2',
            border: '1px solid #fca5a5',
            borderRadius: '8px',
            padding: '1rem',
            marginTop: '1rem',
            display: 'flex',
            alignItems: 'center',
            gap: '0.5rem',
            color: '#dc2626'
          }}>
            <AlertCircle size={20} />
            <span>{apiError}</span>
          </div>
        )}

        <div style={{
          backgroundColor: '#f0f9ff',
          border: '1px solid #0ea5e9',
          borderRadius: '8px',
          padding: '1rem',
          marginTop: '1rem',
          fontSize: '0.875rem',
          color: '#0369a1'
        }}>
          <p style={{ margin: '0 0 0.5rem 0', fontWeight: '500' }}>💡 Sugestões:</p>
          <ul style={{ margin: 0, paddingLeft: '1.5rem' }}>
            <li>Endpoints comuns: <code>/products</code>, <code>/produtos</code>, <code>/itens</code></li>
            <li>Parâmetros de busca suportados: <code>search</code>, <code>q</code>, <code>query</code></li>
          </ul>
        </div>
      </div>

      {completedKits.length > 0 && (
        <div style={{
          backgroundColor: '#f0f9ff',
          padding: '1rem',
          borderRadius: '8px',
          marginBottom: '2rem',
          border: '1px solid #0ea5e9'
        }}>
          <p style={{ margin: '0 0 0.5rem 0', fontWeight: '500', color: '#0369a1' }}>
            ✨ Você já criou {completedKits.length} kit{completedKits.length > 1 ? 's' : ''}!
          </p>
        </div>
      )}

      <button
        onClick={startCreating}
        disabled={availableItems.length === 0}
        style={{
          display: 'flex',
          alignItems: 'center',
          gap: '0.5rem',
          backgroundColor: availableItems.length > 0 ? '#4f46e5' : '#9ca3af',
          color: 'white',
          padding: '1rem 2rem',
          border: 'none',
          borderRadius: '12px',
          cursor: availableItems.length > 0 ? 'pointer' : 'not-allowed',
          fontWeight: '600',
          fontSize: '1.1rem',
          margin: '0 auto',
          transition: 'all 0.2s'
        }}
        onMouseEnter={(e) => { if (availableItems.length > 0) e.currentTarget.style.transform = 'translateY(-2px)'; }}
        onMouseLeave={(e) => e.currentTarget.style.transform = 'translateY(0)'}
      >
        <Plus size={20} />
        {availableItems.length === 0 ? 'Configure o endpoint primeiro' : 'Criar Novo Kit'}
      </button>
    </div>
  );

  const renderCreatingScreen = () => (
    <div style={{ maxWidth: '800px', margin: '0 auto' }}>
      {/* Progress */}
      <div style={{ marginBottom: '2rem', textAlign: 'center' }}>
        <h2 style={{ fontSize: '1.5rem', fontWeight: 'bold', color: '#1f2937', margin: '0 0 0.5rem 0' }}>
          {currentKit.items.length === 0
            ? '🎯 Qual será o primeiro item do seu kit?'
            : `✨ Ótima escolha! Quer adicionar mais algum item? (${currentKit.items.length} itens no kit)`
          }
        </h2>
        {currentKit.generatedName && (
          <div style={{
            backgroundColor: '#f0f9ff',
            padding: '0.75rem 1rem',
            borderRadius: '8px',
            border: '1px solid #0ea5e9',
            margin: '1rem 0'
          }}>
            <p style={{ margin: 0, color: '#0369a1' }}>
              💡 <strong>Nome sugerido:</strong> {currentKit.generatedName}
            </p>
          </div>
        )}
      </div>

      {/* Current Kit Preview */}
      {currentKit.items.length > 0 && (
        <div style={{
          backgroundColor: 'white',
          padding: '1.5rem',
          borderRadius: '12px',
          marginBottom: '2rem',
          boxShadow: '0 2px 10px rgba(0,0,0,0.1)'
        }}>
          <h3 style={{ color: '#1f2937', margin: '0 0 1rem 0', display: 'flex', alignItems: 'center', gap: '0.5rem' }}>
            <Package size={20} />
            Itens no Kit
          </h3>
          <div style={{ display: 'grid', gap: '0.5rem' }}>
            {currentKit.items.map((item, index) => (
              <div key={`${item.id}-${index}`} style={{
                display: 'flex',
                alignItems: 'center',
                gap: '1rem',
                padding: '0.75rem',
                backgroundColor: '#f9fafb',
                borderRadius: '8px'
              }}>
                <span style={{ fontSize: '1.5rem', minWidth: '2rem' }}>
                  {/smartphone|phone|celular/i.test(item.category) ? '📱' :
                   /carregador|charger/i.test(item.category) ? '🔌' :
                   /fone|headphone|earbud/i.test(item.category) ? '🎧' :
                   /case|proteç|protec/i.test(item.category) ? '🛡️' :
                   /bateria|power|bank/i.test(item.category) ? '🔋' : '📦'}
                </span>
                <div style={{ flex: 1 }}>
                  <div style={{ fontWeight: '500', color: '#1f2937' }}>{item.name}</div>
                  <div style={{ fontSize: '0.875rem', color: '#6b7280' }}>{currency(item.price)}</div>
                </div>
                <div style={{ display: 'flex', alignItems: 'center', gap: '0.5rem' }}>
                  <button
                    onClick={() => updateQuantity(item.id, item.quantity - 1)}
                    style={{
                      width: '2rem', height: '2rem', borderRadius: '4px',
                      border: '1px solid #d1d5db', backgroundColor: 'white',
                      cursor: 'pointer', display: 'flex', alignItems: 'center', justifyContent: 'center'
                    }}
                  >-</button>
                  <span style={{ minWidth: '2rem', textAlign: 'center', fontWeight: '500' }}>{item.quantity}</span>
                  <button
                    onClick={() => updateQuantity(item.id, item.quantity + 1)}
                    style={{
                      width: '2rem', height: '2rem', borderRadius: '4px',
                      border: '1px solid #d1d5db', backgroundColor: 'white',
                      cursor: 'pointer', display: 'flex', alignItems: 'center', justifyContent: 'center'
                    }}
                  >+</button>
                </div>
                <button
                  onClick={() => removeItemFromCurrentKit(item.id)}
                  style={{
                    backgroundColor: 'transparent',
                    border: 'none',
                    color: '#ef4444',
                    cursor: 'pointer',
                    padding: '0.25rem'
                  }}
                >
                  <Trash2 size={16} />
                </button>
              </div>
            ))}
          </div>
          <div style={{
            borderTop: '1px solid #e5e7eb',
            marginTop: '1rem',
            paddingTop: '1rem',
            textAlign: 'right'
          }}>
            <span style={{ fontWeight: 'bold', fontSize: '1.1rem', color: '#059669' }}>
              Total: {currency(currentKit.totalPrice)}
            </span>
          </div>
        </div>
      )}

      {/* Search */}
      <div style={{ marginBottom: '1rem' }}>
        <div style={{ position: 'relative' }}>
          <input
            type="text"
            placeholder="🔍 Digite para buscar produtos no apinode..."
            value={searchTerm}
            onChange={(e) => handleSearchChange(e.target.value)}
            style={{
              width: '100%',
              padding: '0.75rem 1rem 0.75rem 3rem',
              border: '2px solid #e5e7eb',
              borderRadius: '12px',
              fontSize: '1rem',
              outline: 'none',
              boxSizing: 'border-box'
            }}
            onFocus={(e) => e.target.style.borderColor = '#4f46e5'}
            onBlur={(e) => e.target.style.borderColor = '#e5e7eb'}
          />
          <div style={{
            position: 'absolute',
            left: '1rem',
            top: '50%',
            transform: 'translateY(-50%)',
            color: '#9ca3af'
          }}>
            {loading ? <Loader size={20} className="animate-spin" /> : <Search size={20} />}
          </div>
        </div>

        {searchTerm && !loading && filteredItems.length === 0 && availableItems.length > 0 && (
          <p style={{ color: '#9ca3af', fontSize: '0.875rem', marginTop: '0.5rem', textAlign: 'center' }}>
            Nenhum produto encontrado para "{searchTerm}"
          </p>
        )}
      </div>

      {/* Available Items */}
      {loading && availableItems.length === 0 ? (
        <div style={{
          display: 'flex',
          justifyContent: 'center',
          alignItems: 'center',
          padding: '3rem',
          backgroundColor: 'white',
          borderRadius: '12px',
          marginBottom: '2rem'
        }}>
          <Loader size={32} className="animate-spin" style={{ marginRight: '1rem' }} />
          <span style={{ fontSize: '1.1rem', color: '#6b7280' }}>Carregando produtos do apinode...</span>
        </div>
      ) : filteredItems.length === 0 && !loading ? (
        <div style={{
          backgroundColor: 'white',
          borderRadius: '12px',
          padding: '3rem',
          textAlign: 'center',
          border: '2px dashed #d1d5db',
          color: '#6b7280',
          marginBottom: '2rem'
        }}>
          <Package size={48} color="#d1d5db" style={{ margin: '0 auto 1rem' }} />
          <p style={{ fontSize: '1.1rem', margin: 0 }}>
            {availableItems.length === 0
              ? 'Defina o endpoint do apinode para carregar os produtos'
              : searchTerm
                ? `Nenhum produto encontrado para "${searchTerm}"`
                : 'Nenhum produto disponível'
            }
          </p>
        </div>
      ) : (
        <div style={{
          display: 'grid',
          gridTemplateColumns: 'repeat(auto-fill, minmax(280px, 1fr))',
          gap: '1rem',
          marginBottom: '2rem'
        }}>
          {filteredItems.map(item => {
            const isInKit = currentKit.items.some(kitItem => kitItem.id === item.id);
            return (
              <div
                key={item.id}
                onClick={() => !isInKit && addItemToKit(item)}
                style={{
                  padding: '1.2rem',
                  backgroundColor: isInKit ? '#f3f4f6' : 'white',
                  border: isInKit ? '2px solid #9ca3af' : '2px solid #e5e7eb',
                  borderRadius: '12px',
                  cursor: isInKit ? 'not-allowed' : 'pointer',
                  transition: 'all 0.2s',
                  opacity: isInKit ? 0.6 : 1
                }}
                onMouseEnter={(e) => {
                  if (!isInKit) {
                    e.currentTarget.style.borderColor = '#4f46e5';
                    e.currentTarget.style.transform = 'translateY(-2px)';
                  }
                }}
                onMouseLeave={(e) => {
                  if (!isInKit) {
                    e.currentTarget.style.borderColor = '#e5e7eb';
                    e.currentTarget.style.transform = 'translateY(0)';
                  }
                }}
              >
                {item.image && (
                  <div style={{ marginBottom: '0.75rem', textAlign: 'center' }}>
                    <img
                      src={item.image}
                      alt={item.name}
                      style={{
                        width: '60px',
                        height: '60px',
                        objectFit: 'cover',
                        borderRadius: '8px',
                        backgroundColor: '#f9fafb'
                      }}
                      onError={(e) => { e.currentTarget.style.display = 'none'; }}
                    />
                  </div>
                )}

                <div style={{ display: 'flex', alignItems: 'flex-start', gap: '0.75rem', marginBottom: '0.5rem' }}>
                  <span style={{ fontSize: '2rem' }}>
                    {/smartphone|phone|celular/i.test(item.category) ? '📱' :
                     /carregador|charger/i.test(item.category) ? '🔌' :
                     /fone|headphone|earbud/i.test(item.category) ? '🎧' :
                     /case|proteç|protec/i.test(item.category) ? '🛡️' :
                     /bateria|power|bank/i.test(item.category) ? '🔋' : '📦'}
                  </span>
                  <div style={{ flex: 1 }}>
                    <div style={{ fontWeight: '600', color: '#1f2937', marginBottom: '0.25rem', lineHeight: '1.3' }}>
                      {item.name}
                    </div>
                    <div style={{ fontSize: '0.875rem', color: '#6b7280', marginBottom: '0.25rem' }}>
                      {item.category}{item.brand ? ` • ${item.brand}` : ''}
                    </div>
                    {item.sku && (
                      <div style={{ fontSize: '0.75rem', color: '#9ca3af' }}>
                        SKU: {item.sku}
                      </div>
                    )}
                  </div>
                  {isInKit && <Check size={20} color="#059669" />}
                </div>

                <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                  <div style={{ fontWeight: 'bold', color: '#059669', fontSize: '1.1rem' }}>
                    {currency(item.price)}
                  </div>
                  {item.stock !== undefined && (
                    <div style={{ fontSize: '0.75rem', color: item.stock > 0 ? '#059669' : '#ef4444' }}>
                      {item.stock > 0 ? `${item.stock} em estoque` : 'Sem estoque'}
                    </div>
                  )}
                </div>
              </div>
            );
          })}
        </div>
      )}

      {/* Actions */}
      <div style={{ display: 'flex', gap: '1rem', justifyContent: 'center' }}>
        <button
          onClick={() => setCurrentStep('start')}
          style={{
            display: 'flex',
            alignItems: 'center',
            gap: '0.5rem',
            backgroundColor: '#6b7280',
            color: 'white',
            padding: '0.75rem 1.5rem',
            border: 'none',
            borderRadius: '8px',
            cursor: 'pointer',
            fontWeight: '500'
          }}
        >
          <ArrowLeft size={16} />
          Cancelar
        </button>

        {currentKit.items.length > 0 && (
          <button
            onClick={goToReview}
            style={{
              display: 'flex',
              alignItems: 'center',
              gap: '0.5rem',
              backgroundColor: '#059669',
              color: 'white',
              padding: '0.75rem 1.5rem',
              border: 'none',
              borderRadius: '8px',
              cursor: 'pointer',
              fontWeight: '500'
            }}
          >
            Finalizar Kit
            <ArrowRight size={16} />
          </button>
        )}
      </div>
    </div>
  );

  const renderReviewScreen = () => (
    <div style={{ maxWidth: '600px', margin: '0 auto', textAlign: 'center' }}>
      <h2 style={{ fontSize: '2rem', fontWeight: 'bold', color: '#1f2937', margin: '0 0 2rem 0' }}>
        🎉 Kit Criado com Sucesso!
      </h2>

      <div style={{
        backgroundColor: 'white',
        padding: '2rem',
        borderRadius: '12px',
        boxShadow: '0 4px 20px rgba(0,0,0,0.1)',
        marginBottom: '2rem'
      }}>
        <div style={{
          backgroundColor: '#f0f9ff',
          padding: '1rem',
          borderRadius: '8px',
          marginBottom: '1.5rem',
          border: '2px solid #0ea5e9'
        }}>
          <h3 style={{ color: '#0369a1', margin: '0 0 0.5rem 0', display: 'flex', alignItems: 'center', justifyContent: 'center', gap: '0.5rem' }}>
            <Edit3 size={20} />
            Nome do Anúncio
          </h3>
          <p style={{ fontSize: '1.2rem', fontWeight: 'bold', color: '#0c4a6e', margin: 0 }}>
            {currentKit.generatedName}
          </p>
        </div>

        <div style={{ textAlign: 'left' }}>
          <h4 style={{ color: '#1f2937', margin: '0 0 1rem 0' }}>Itens do Kit:</h4>
          {currentKit.items.map(item => (
            <div key={item.id} style={{
              display: 'flex',
              justifyContent: 'space-between',
              alignItems: 'center',
              padding: '0.5rem 0',
              borderBottom: '1px solid #f3f4f6'
            }}>
              <span>{item.name} {item.quantity > 1 && `(${item.quantity}x)`}</span>
              <span style={{ fontWeight: '500', color: '#059669' }}>
                {currency(item.price * item.quantity)}
              </span>
            </div>
          ))}
        </div>

        <div style={{
          borderTop: '2px solid #e5e7eb',
          marginTop: '1rem',
          paddingTop: '1rem',
          fontSize: '1.2rem',
          fontWeight: 'bold',
          color: '#059669'
        }}>
          Total: {currency(currentKit.totalPrice)}
        </div>
      </div>

      <div style={{ display: 'flex', gap: '1rem', justifyContent: 'center' }}>
        <button
          onClick={() => setCurrentStep('creating')}
          style={{
            display: 'flex',
            alignItems: 'center',
            gap: '0.5rem',
            backgroundColor: '#6b7280',
            color: 'white',
            padding: '0.75rem 1.5rem',
            border: 'none',
            borderRadius: '8px',
            cursor: 'pointer',
            fontWeight: '500'
          }}
        >
          <ArrowLeft size={16} />
          Voltar
        </button>

        <button
          onClick={completeKit}
          style={{
            display: 'flex',
            alignItems: 'center',
            gap: '0.5rem',
            backgroundColor: '#059669',
            color: 'white',
            padding: '0.75rem 1.5rem',
            border: 'none',
            borderRadius: '8px',
            cursor: 'pointer',
            fontWeight: '500'
          }}
        >
          <Check size={16} />
          Confirmar Kit
        </button>
      </div>
    </div>
  );

  const renderCompleteScreen = () => (
    <div style={{ maxWidth: '600px', margin: '0 auto', textAlign: 'center' }}>
      <div style={{ marginBottom: '2rem' }}>
        <div style={{ fontSize: '4rem', marginBottom: '1rem' }}>🎊</div>
        <h2 style={{ fontSize: '2rem', fontWeight: 'bold', color: '#1f2937', margin: '0 0 1rem 0' }}>
          Kit Salvo com Sucesso!
        </h2>
        <p style={{ color: '#6b7280', fontSize: '1.1rem', margin: 0 }}>
          Seu kit foi criado e está pronto para ser usado em seus anúncios
        </p>
      </div>

      <div style={{
        backgroundColor: '#f0fdf4',
        padding: '1.5rem',
        borderRadius: '12px',
        border: '1px solid #22c55e',
        marginBottom: '2rem'
      }}>
        <p style={{ margin: '0 0 0.5rem 0', color: '#15803d', fontWeight: '500' }}>
          ✅ Total de kits criados: {completedKits.length}
        </p>
        <p style={{ margin: 0, color: '#15803d' }}>
          📝 Último kit: "{completedKits[completedKits.length - 1]?.generatedName}"
        </p>
      </div>

      <div style={{ display: 'flex', gap: '1rem', justifyContent: 'center' }}>
        <button
          onClick={createAnother}
          style={{
            display: 'flex',
            alignItems: 'center',
            gap: '0.5rem',
            backgroundColor: '#4f46e5',
            color: 'white',
            padding: '1rem 2rem',
            border: 'none',
            borderRadius: '12px',
            cursor: 'pointer',
            fontWeight: '600',
            fontSize: '1.1rem'
          }}
        >
          <Plus size={20} />
          Criar Outro Kit
        </button>
      </div>

      {completedKits.length > 0 && (
        <div style={{
          marginTop: '3rem',
          backgroundColor: 'white',
          padding: '1.5rem',
          borderRadius: '12px',
          boxShadow: '0 2px 10px rgba(0,0,0,0.1)'
        }}>
          <h3 style={{ margin: '0 0 1rem 0', color: '#1f2937' }}>Kits Criados</h3>
          <div style={{ display: 'flex', flexDirection: 'column', gap: '0.5rem' }}>
            {completedKits.map(kit => (
              <div key={kit.id} style={{
                display: 'flex',
                justifyContent: 'space-between',
                alignItems: 'center',
                padding: '0.75rem',
                backgroundColor: '#f9fafb',
                borderRadius: '8px'
              }}>
                <div style={{ textAlign: 'left' }}>
                  <div style={{ fontWeight: '500', color: '#1f2937' }}>{kit.generatedName}</div>
                  <div style={{ fontSize: '0.875rem', color: '#6b7280' }}>
                    {kit.items.length} itens • {kit.createdAt}
                  </div>
                </div>
                <div style={{ fontWeight: 'bold', color: '#059669' }}>
                  {currency(kit.totalPrice)}
                </div>
              </div>
            ))}
          </div>
        </div>
      )}
    </div>
  );

  return (
    <div style={{
      minHeight: '100vh',
      backgroundColor: '#f8fafc',
      fontFamily: 'system-ui, -apple-system, sans-serif',
      padding: '2rem'
    }}>
      <div style={{ maxWidth: '1200px', margin: '0 auto' }}>
        {/* Header */}
        <div style={{
          textAlign: 'center',
          marginBottom: '3rem',
          backgroundColor: 'white',
          padding: '2rem',
          borderRadius: '12px',
          boxShadow: '0 2px 10px rgba(0,0,0,0.1)'
        }}>
          <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', gap: '0.5rem', marginBottom: '0.5rem' }}>
            <Wand2 size={32} color="#4f46e5" />
            <h1 style={{
              fontSize: '2.5rem',
              fontWeight: 'bold',
              color: '#1f2937',
              margin: 0
            }}>
              Kit Creator Pro
            </h1>
          </div>
          <p style={{ color: '#6b7280', margin: 0, fontSize: '1.1rem' }}>
            Criação inteligente de kits com nomes automáticos para seus anúncios
          </p>
        </div>

        {/* Steps */}
        {currentStep === 'start' && renderStartScreen()}
        {currentStep === 'creating' && renderCreatingScreen()}
        {currentStep === 'review' && renderReviewScreen()}
        {currentStep === 'complete' && renderCompleteScreen()}
      </div>
    </div>
  );
};

export default KitCreator;
